(() => {
  const QUALIFYING_HANDLES = new Set([
    'clean-coastal-inflatable-beach-chairs',
    'clean-coastal-beach-towel',
    'pop-up-beach-trash-bin'
  ]);
  const BEACHBIN_HANDLE = 'pop-up-beach-trash-bin';
  const BLANKET_HANDLE = 'clean-coastal-beach-blanket';
  const CHAIR_HANDLE = 'clean-coastal-inflatable-beach-chairs';
  const PAIR_KIT_CODE = 'PAIRKIT1998';
  const PAIR_KIT_ATTRIBUTE = '_cc_offer';
  const PAIR_KIT_VALUE = 'pair_kit';
  const QUALIFIER_ORDER_STORAGE_KEY = 'cc-blanket-upsell-qualifier-order';

  const normalize = (value) => String(value || '').trim().toLowerCase();
  const routesRoot = () => window.Shopify?.routes?.root || '/';
  const route = (path) => routesRoot() + path.replace(/^\/+/, '');

  const getAppliedCodes = (cart) => {
    const codes = (cart.discount_codes || [])
      .map((entry) => entry && (entry.code || entry.title))
      .filter(Boolean);
    const applications = [...(cart.cart_level_discount_applications || [])];

    (cart.items || []).forEach((item) => {
      (item.line_level_discount_allocations || []).forEach((allocation) => {
        if (allocation.discount_application) applications.push(allocation.discount_application);
      });
    });

    applications
      .filter((application) => application && application.type === 'discount_code')
      .forEach((application) => {
        if (application.title) codes.push(application.title);
      });

    return [...new Set(codes)];
  };

  const pairKitState = (cart) => {
    const chairQuantity = (cart.items || [])
      .filter((item) => item.handle === CHAIR_HANDLE)
      .reduce((total, item) => total + Number(item.quantity || 0), 0);
    const accessoryQuantity = (cart.items || [])
      .filter((item) => item.handle === BEACHBIN_HANDLE || item.handle === BLANKET_HANDLE)
      .reduce((total, item) => total + Number(item.quantity || 0), 0);
    const marked = cart.attributes && cart.attributes[PAIR_KIT_ATTRIBUTE] === PAIR_KIT_VALUE;

    return {
      marked,
      valid: marked && chairQuantity === 2 && accessoryQuantity >= 1
    };
  };

  const syncPairKit = async (cart) => {
    const state = pairKitState(cart);
    const codes = getAppliedCodes(cart);
    const hasPairCode = codes.some((code) => normalize(code) === normalize(PAIR_KIT_CODE));
    const conflictingCodes = codes.some((code) => normalize(code) !== normalize(PAIR_KIT_CODE));
    const shouldUpdate = state.valid
      ? (!hasPairCode || conflictingCodes)
      : (hasPairCode || state.marked);

    if (!shouldUpdate) return { cart, changed: false, valid: state.valid };

    const nextCodes = state.valid
      ? [PAIR_KIT_CODE]
      : codes.filter((code) => normalize(code) !== normalize(PAIR_KIT_CODE));

    const body = {
      discount: nextCodes.join(','),
      sections: 'cart-drawer,cart-icon-bubble',
      sections_url: window.location.pathname || '/cart'
    };

    if (!state.valid && state.marked) {
      body.attributes = { [PAIR_KIT_ATTRIBUTE]: '' };
    }

    const response = await fetch(route('cart/update.js'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify(body)
    });
    const updated = await response.json();
    if (!response.ok) throw new Error(updated.description || 'Unable to update the Pair Kit offer.');

    return { cart: updated, changed: true, valid: state.valid };
  };

  const formatMoney = (cents, currency = 'USD') =>
    new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
      style: 'currency',
      currency
    }).format(Number(cents || 0) / 100);

  class CCCartBlanketUpsell extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ccInitialized === 'true') return;
      this.dataset.ccInitialized = 'true';

      try {
        const data = JSON.parse(this.querySelector('[data-cc-upsell-variants]').textContent);
        this.variants = Array.isArray(data.variants) ? data.variants : [];
      } catch (error) {
        this.variants = [];
      }

      this.discountCode = this.dataset.discountCode || '';
      this.button = this.querySelector('[data-cc-upsell-add]');
      this.status = this.querySelector('[data-cc-upsell-status]');
      this.swatches = [...this.querySelectorAll('[data-cc-upsell-swatch]')];

      this.button?.addEventListener('click', () => this.addBlanket());
      this.swatches.forEach((swatch) => {
        swatch.addEventListener('click', () => {
          const variant = this.variants.find(
            (candidate) => String(candidate.id) === String(swatch.dataset.ccUpsellSwatch)
          );
          if (variant?.available) this.renderOffer(variant, this.currency);
        });
      });

      this.refresh();
    }

    async getCart() {
      const response = await fetch(route('cart.js'), {
        headers: { Accept: 'application/json' }
      });
      if (!response.ok) throw new Error('Unable to load your cart.');
      return response.json();
    }

    getAppliedCodes(cart) {
      return getAppliedCodes(cart);
    }

    getColor(item) {
      if (item.handle === BEACHBIN_HANDLE) return 'light blue';

      const colorOption = (item.options_with_values || []).find(
        (option) => normalize(option.name) === 'color'
      );
      return colorOption?.value || item.variant_options?.[0] || '';
    }

    getQualifierId(item) {
      return String(item.variant_id || item.id || item.key || '');
    }

    getFirstQualifier(cart) {
      const qualifiers = (cart.items || []).filter((item) =>
        QUALIFYING_HANDLES.has(item.handle)
      );

      if (!qualifiers.length) {
        try {
          sessionStorage.removeItem(QUALIFIER_ORDER_STORAGE_KEY);
        } catch (error) {
          // Storage can be unavailable in privacy-restricted browsers.
        }
        return null;
      }

      const fallback = qualifiers[qualifiers.length - 1];
      let orderedIds = [];

      try {
        const stored = JSON.parse(
          sessionStorage.getItem(QUALIFIER_ORDER_STORAGE_KEY) || '{}'
        );
        if (stored.cartToken === cart.token && Array.isArray(stored.variantIds)) {
          orderedIds = stored.variantIds.map(String);
        }
      } catch (error) {
        orderedIds = [];
      }

      const presentIds = new Set(qualifiers.map((item) => this.getQualifierId(item)));
      orderedIds = orderedIds.filter((id) => presentIds.has(id));

      const knownIds = new Set(orderedIds);
      [...qualifiers].reverse().forEach((item) => {
        const id = this.getQualifierId(item);
        if (id && !knownIds.has(id)) {
          orderedIds.push(id);
          knownIds.add(id);
        }
      });

      try {
        sessionStorage.setItem(
          QUALIFIER_ORDER_STORAGE_KEY,
          JSON.stringify({
            cartToken: cart.token,
            variantIds: orderedIds
          })
        );
      } catch (error) {
        // The fallback below still preserves Shopify's cart-item order.
      }

      return (
        orderedIds
          .map((id) => qualifiers.find((item) => this.getQualifierId(item) === id))
          .find(Boolean) || fallback
      );
    }

    getMatchingVariant(color) {
      const available = this.variants.filter((variant) => variant.available);
      return available.find((variant) => normalize(variant.color) === normalize(color)) || available[0];
    }

    hasCode(codes) {
      return codes.some((code) => normalize(code) === normalize(this.discountCode));
    }

    async updateDiscounts(codes, addCode, render = true) {
      const nextCodes = codes.filter(
        (code) => normalize(code) !== normalize(this.discountCode)
      );
      if (addCode) nextCodes.push(this.discountCode);

      const response = await fetch(route('cart/update.js'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          discount: nextCodes.join(','),
          sections: 'cart-drawer,cart-icon-bubble',
          sections_url: window.location.pathname || '/cart'
        })
      });

      const cart = await response.json();
      if (!response.ok) throw new Error(cart.description || 'Unable to update the discount.');
      if (render) this.renderCart(cart);
      return cart;
    }

    renderCart(cart) {
      if (document.querySelector('cart-items')) {
        window.location.reload();
        return;
      }

      const drawer = document.querySelector('cart-drawer');
      if (drawer?.renderContents && cart.sections) {
        drawer.renderContents(cart);
        return;
      }

      window.location.href = route('cart');
    }

    renderOffer(variant, currency) {
      this.currentVariant = variant;
      this.currency = currency || this.currency || window.Shopify?.currency?.active || 'USD';

      const salePrice = Math.max(0, Number(variant.price || 0) - 1000);
      const image = this.querySelector('[data-cc-upsell-image]');

      if (image && variant.image) image.src = variant.image;
      if (image && variant.alt) image.alt = variant.alt;

      const variantText = this.querySelector('[data-cc-upsell-variant]');
      if (variantText) variantText.textContent = String(variant.color || 'Matching color') + ' selected';

      const originalPrice = this.querySelector('[data-cc-upsell-original]');
      if (originalPrice) originalPrice.textContent = formatMoney(variant.price, this.currency);

      const salePriceElement = this.querySelector('[data-cc-upsell-sale]');
      if (salePriceElement) salePriceElement.textContent = formatMoney(salePrice, this.currency);

      this.swatches.forEach((swatch) => {
        const selected = String(swatch.dataset.ccUpsellSwatch) === String(variant.id);
        swatch.classList.toggle('is-selected', selected);
        swatch.setAttribute('aria-pressed', String(selected));
      });

      if (this.button) {
        this.button.textContent = 'Add for ' + formatMoney(salePrice, this.currency);
      }
      this.hidden = false;
    }

    async refresh() {
      if (this.refreshing || !this.variants.length) return;
      this.refreshing = true;

      try {
        let cart = await this.getCart();
        const pairSync = await syncPairKit(cart);
        cart = pairSync.cart;

        if (pairSync.changed) {
          this.hidden = true;
          this.renderCart(cart);
          return;
        }

        if (pairSync.valid || pairKitState(cart).marked) {
          this.hidden = true;
          return;
        }

        const qualifier = this.getFirstQualifier(cart);
        const blanket = (cart.items || []).find((item) => item.handle === BLANKET_HANDLE);
        const codes = this.getAppliedCodes(cart);
        const codeIsActive = this.hasCode(codes);
        const shouldBeActive = Boolean(qualifier && blanket);

        if (codeIsActive !== shouldBeActive) {
          await this.updateDiscounts(codes, shouldBeActive);
          return;
        }

        const chairQuantity = (cart.items || [])
          .filter((item) => item.handle === CHAIR_HANDLE)
          .reduce((total, item) => total + Number(item.quantity || 0), 0);

        if (chairQuantity === 1 && !blanket) {
          this.hidden = true;
          return;
        }

        if (!qualifier || blanket) {
          this.hidden = true;
          return;
        }

        const variant = this.getMatchingVariant(this.getColor(qualifier));
        if (!variant) {
          this.hidden = true;
          return;
        }

        this.renderOffer(variant, cart.currency || window.Shopify?.currency?.active || 'USD');
      } catch (error) {
        this.hidden = true;
      } finally {
        this.refreshing = false;
      }
    }

    async addBlanket() {
      if (!this.currentVariant || this.button?.disabled) return;
      this.button.disabled = true;
      this.button.textContent = 'Adding…';
      if (this.status) this.status.textContent = '';

      try {
        const before = await this.getCart();
        const codes = this.getAppliedCodes(before);

        const addResponse = await fetch(route('cart/add.js'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({
            items: [{ id: String(this.currentVariant.id), quantity: 1 }]
          })
        });
        const added = await addResponse.json();
        if (!addResponse.ok) throw new Error(added.description || 'Unable to add the blanket.');

        await this.updateDiscounts(codes, true);
      } catch (error) {
        if (this.status) {
          this.status.textContent = error.message || 'Something went wrong. Please try again.';
        }
        this.button.disabled = false;
        const salePrice = Math.max(0, Number(this.currentVariant.price || 0) - 1000);
        this.button.textContent = 'Add for ' + formatMoney(salePrice, this.currency || 'USD');
      }
    }
  }

  window.CleanCoastalPairKit = { sync: syncPairKit, state: pairKitState };

  if (!customElements.get('cc-cart-blanket-upsell')) {
    customElements.define('cc-cart-blanket-upsell', CCCartBlanketUpsell);
  }
})();
